import React, { useEffect, useState } from "react";
import {
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import { useTranslation } from "react-i18next";

interface PasswordStrengthCheckerProps {
  password: string;
}

const PasswordStrengthChecker: React.FC<PasswordStrengthCheckerProps> = ({
  password,
}) => {
  const { t } = useTranslation();
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [requirements, setRequirements] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    numeric: false,
    special: false,
  });

  useEffect(() => {
    const length = password.length >= 8;
    const uppercase = /[A-Z]/.test(password);
    const lowercase = /[a-z]/.test(password);
    const numeric = /\d/.test(password);
    const special = /[^A-Za-z0-9]/.test(password);

    setRequirements({ length, uppercase, lowercase, numeric, special });

    const fulfilledRequirements = Object.values({
      length,
      uppercase,
      lowercase,
      numeric,
      special,
    }).filter(Boolean).length;

    setPasswordStrength((fulfilledRequirements / 5) * 100);
  }, [password]);

  const getStrengthLabel = () => {
    if (passwordStrength < 21) return t("passwordStrength.veryWeak");
    if (passwordStrength < 41) return t("passwordStrength.weak");
    if (passwordStrength < 61) return t("passwordStrength.normal");
    if (passwordStrength < 81) return t("passwordStrength.strong");
    return t("passwordStrength.veryStrong");
  };

  return (
    <>
      <Typography variant="body2" align="right">
        {getStrengthLabel()}
      </Typography>
      <LinearProgress
        variant="determinate"
        value={passwordStrength}
        sx={{
          my: 1,
          bgcolor: "#f0f0f0",
          "& .MuiLinearProgress-bar": {
            bgcolor:
              passwordStrength === 100
                ? "#008000"
                : passwordStrength >= 80
                ? "#32CD32"
                : passwordStrength >= 60
                ? "#FFD700"
                : passwordStrength >= 40
                ? "#FF8C00"
                : "#FF4500",
          },
        }}
      />
      <List
        sx={{
          ".MuiListItemIcon-root": {
            minWidth: 24,
            svg: {
              width: 16,
            },
          },
          ".MuiListItem-root": { padding: 0 },
          "& .MuiListItem-root span": { fontSize: 13 },
        }}
      >
        <ListItem>
          <ListItemIcon>
            {requirements.length ? (
              <CheckCircleIcon color="success" />
            ) : (
              <CancelIcon color="error" />
            )}
          </ListItemIcon>
          <ListItemText primary={t("passwordStrength.requirements.length")} />
        </ListItem>
        <ListItem>
          <ListItemIcon>
            {requirements.uppercase ? (
              <CheckCircleIcon color="success" />
            ) : (
              <CancelIcon color="error" />
            )}
          </ListItemIcon>
          <ListItemText primary={t("passwordStrength.requirements.uppercase")} />
        </ListItem>
        <ListItem>
          <ListItemIcon>
            {requirements.lowercase ? (
              <CheckCircleIcon color="success" />
            ) : (
              <CancelIcon color="error" />
            )}
          </ListItemIcon>
          <ListItemText primary={t("passwordStrength.requirements.lowercase")} />
        </ListItem>
        <ListItem>
          <ListItemIcon>
            {requirements.numeric ? (
              <CheckCircleIcon color="success" />
            ) : (
              <CancelIcon color="error" />
            )}
          </ListItemIcon>
          <ListItemText primary={t("passwordStrength.requirements.numeric")} />
        </ListItem>
        <ListItem>
          <ListItemIcon>
            {requirements.special ? (
              <CheckCircleIcon color="success" />
            ) : (
              <CancelIcon color="error" />
            )}
          </ListItemIcon>
          <ListItemText primary={t("passwordStrength.requirements.special")} />
        </ListItem>
      </List>
    </>
  );
};

export default PasswordStrengthChecker;
